import { useMemo, useState, type ReactNode } from 'react';
import { formatDuration, isKitchenRoom, riyadhBusinessDate } from '@room-verify/shared';
import { api, type EditRecord, type InspectionListItem, type ListFilters } from '../api';
import { ar, arDate, arDateTime } from '../ar';
import { AuditTimeline } from '../components/audit';
import { FilterBar } from '../components/filters';
import {
  IconAudit,
  IconCalendar,
  IconInspections,
  IconLogout,
  IconRefresh,
  IconRooms,
  IconSupervisors,
} from '../components/icons';
import { InspectionList, InspectionListSkeleton } from '../components/inspections';
import {
  Button,
  Chip,
  EmptyState,
  Notice,
  Skeleton,
  StatusIndicator,
} from '../components/primitives';
import { TopBar } from '../components/shell';
import { useAsync } from '../hooks';

/**
 * The list-based destinations.
 *
 * Every one of them is backed by an endpoint that already exists. Rooms and the audit
 * log are derived from the inspection list rather than invented — no route here implies
 * functionality the API cannot serve.
 */

interface CommonProps {
  token: string;
  onOpenDrawer: () => void;
  onAuthExpired: () => void;
  onOpenReport: (id: string) => void;
  onEdit: (item: InspectionListItem) => void;
  reloadKey: number;
}

/* ------------------------------------------------- Inspections (today) */

/** Today's operational feed: no date range, just what is happening now. */
export function InspectionsScreen({
  token,
  onOpenDrawer,
  onAuthExpired,
  onOpenReport,
  onEdit,
  reloadKey,
}: CommonProps) {
  const today = riyadhBusinessDate();
  const [filters, setFilters] = useState<ListFilters>({ from: today, to: today });
  const defaults: ListFilters = { from: today, to: today };

  const supervisors = useAsync(() => api.supervisors(token), [token], onAuthExpired);
  const list = useAsync(
    () => api.inspections(token, { ...filters, limit: 300 }),
    [token, JSON.stringify(filters), reloadKey],
    onAuthExpired,
  );

  const items = list.data?.results ?? [];

  return (
    <>
      <TopBar
        title={ar.navInspections}
        subtitle={arDate(`${filters.from ?? today}T09:00:00.000Z`)}
        onOpenDrawer={onOpenDrawer}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={list.reload}
            aria-label={ar.refresh}
            icon={<IconRefresh size={15} />}
          />
        }
      />
      <div className="page page-wide">
        <FilterBar
          filters={filters}
          defaults={defaults}
          supervisors={supervisors.data?.supervisors ?? []}
          onChange={setFilters}
          onClear={() => setFilters(defaults)}
        />

        {list.error ? (
          <Notice tone="critical" role="alert">
            {list.error}
          </Notice>
        ) : list.loading && items.length === 0 ? (
          <InspectionListSkeleton rows={6} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<IconInspections size={26} />}
            title={ar.noReportsTitle}
            text={ar.noReportsText}
            action={
              <Button variant="outline" size="sm" onClick={() => setFilters(defaults)}>
                {ar.clearFilters}
              </Button>
            }
          />
        ) : (
          <InspectionList items={items} onOpen={onOpenReport} onEdit={onEdit} />
        )}
      </div>
    </>
  );
}

/* --------------------------------------------------- Reports (archive) */

/** The searchable archive: same endpoint, opened wide and grouped by day. */
export function ReportsScreen({
  token,
  onOpenDrawer,
  onAuthExpired,
  onOpenReport,
  onEdit,
  reloadKey,
  initialFilters,
}: CommonProps & { initialFilters?: Partial<ListFilters> }) {
  const today = riyadhBusinessDate();
  const monthAgo = useMemo(() => {
    const date = new Date(`${today}T12:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() - 29);
    return date.toISOString().slice(0, 10);
  }, [today]);

  const defaults: ListFilters = { from: monthAgo, to: today };
  const [filters, setFilters] = useState<ListFilters>({ ...defaults, ...initialFilters });

  const supervisors = useAsync(() => api.supervisors(token), [token], onAuthExpired);
  const list = useAsync(
    () => api.inspections(token, { ...filters, limit: 400 }),
    [token, JSON.stringify(filters), reloadKey],
    onAuthExpired,
  );

  const items = list.data?.results ?? [];
  const total = list.data?.total ?? 0;

  return (
    <>
      <TopBar
        title={ar.navReports}
        subtitle={list.data ? ar.resultsCount(items.length, total) : undefined}
        onOpenDrawer={onOpenDrawer}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={list.reload}
            aria-label={ar.refresh}
            icon={<IconRefresh size={15} />}
          />
        }
      />
      <div className="page page-wide">
        <FilterBar
          filters={filters}
          defaults={defaults}
          supervisors={supervisors.data?.supervisors ?? []}
          onChange={setFilters}
          onClear={() => setFilters(defaults)}
        />

        {list.error ? (
          <Notice tone="critical" role="alert">
            {list.error}
          </Notice>
        ) : list.loading && items.length === 0 ? (
          <InspectionListSkeleton rows={8} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<IconCalendar size={26} />}
            title={ar.noResultsTitle}
            text={ar.noResultsText}
            action={
              <Button variant="outline" size="sm" onClick={() => setFilters(defaults)}>
                {ar.clearFilters}
              </Button>
            }
          />
        ) : (
          <InspectionList items={items} onOpen={onOpenReport} onEdit={onEdit} groupByDate />
        )}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- Rooms */

interface RoomSummary {
  roomNumber: string;
  total: number;
  missing: number;
  lastAt: string;
  kitchen: boolean;
}

/** Per-room aggregates, computed from the inspection list. No new endpoint. */
export function RoomsScreen({
  token,
  onOpenDrawer,
  onAuthExpired,
  onOpenReport,
  reloadKey,
}: Omit<CommonProps, 'onEdit'>) {
  const today = riyadhBusinessDate();
  const from = useMemo(() => {
    const date = new Date(`${today}T12:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() - 29);
    return date.toISOString().slice(0, 10);
  }, [today]);

  const list = useAsync(
    () => api.inspections(token, { from, to: today, limit: 500 }),
    [token, from, today, reloadKey],
    onAuthExpired,
  );

  const rooms = useMemo<RoomSummary[]>(() => {
    const map = new Map<string, RoomSummary>();
    for (const item of list.data?.results ?? []) {
      const existing = map.get(item.roomNumber);
      if (existing) {
        existing.total += 1;
        if (!item.itemsComplete) existing.missing += 1;
        if (item.completedAt > existing.lastAt) existing.lastAt = item.completedAt;
      } else {
        map.set(item.roomNumber, {
          roomNumber: item.roomNumber,
          total: 1,
          missing: item.itemsComplete ? 0 : 1,
          lastAt: item.completedAt,
          kitchen: isKitchenRoom(item.roomNumber),
        });
      }
    }
    return [...map.values()].sort((a, b) => a.roomNumber.localeCompare(b.roomNumber, 'en'));
  }, [list.data]);

  const latestByRoom = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of list.data?.results ?? []) {
      const current = map.get(item.roomNumber);
      if (!current) map.set(item.roomNumber, item.id);
    }
    return map;
  }, [list.data]);

  return (
    <>
      <TopBar
        title={ar.navRooms}
        subtitle={ar.roomsSubtitle}
        onOpenDrawer={onOpenDrawer}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={list.reload}
            aria-label={ar.refresh}
            icon={<IconRefresh size={15} />}
          />
        }
      />
      <div className="page page-wide">
        {list.error ? (
          <Notice tone="critical" role="alert">
            {list.error}
          </Notice>
        ) : list.loading && rooms.length === 0 ? (
          <div className="tile-grid">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} height={104} radius={16} />
            ))}
          </div>
        ) : rooms.length === 0 ? (
          <EmptyState icon={<IconRooms size={26} />} title={ar.noRoomsTitle} text={ar.noRoomsText} />
        ) : (
          <ul className="tile-grid">
            {rooms.map((room) => {
              const reportId = latestByRoom.get(room.roomNumber);
              return (
                <li key={room.roomNumber}>
                  <button
                    className="room-tile"
                    onClick={() => reportId && onOpenReport(reportId)}
                    disabled={!reportId}
                  >
                    <span className="room-tile-head">
                      <span className="room-tile-number num" dir="ltr">
                        {room.roomNumber}
                      </span>
                      {room.kitchen && <Chip tone="neutral">{ar.kitchenRoom}</Chip>}
                    </span>
                    <span className="room-tile-stats">
                      <span className="room-tile-stat">
                        <span className="room-tile-value num">{room.total}</span>
                        <span className="room-tile-label">{ar.inspectionsCount}</span>
                      </span>
                      {room.missing > 0 && (
                        <span className="room-tile-stat is-warning">
                          <span className="room-tile-value num">{room.missing}</span>
                          <span className="room-tile-label">{ar.missingItemsCount}</span>
                        </span>
                      )}
                    </span>
                    <span className="room-tile-foot num">
                      {ar.lastInspection} · {arDateTime(room.lastAt)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

/* ---------------------------------------------------------- Supervisors */

export function SupervisorsScreen({
  token,
  onOpenDrawer,
  onAuthExpired,
  reloadKey,
}: Pick<CommonProps, 'token' | 'onOpenDrawer' | 'onAuthExpired' | 'reloadKey'>) {
  const today = riyadhBusinessDate();
  const from = useMemo(() => {
    const date = new Date(`${today}T12:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() - 29);
    return date.toISOString().slice(0, 10);
  }, [today]);

  const supervisors = useAsync(() => api.supervisors(token), [token, reloadKey], onAuthExpired);
  const list = useAsync(
    () => api.inspections(token, { from, to: today, limit: 500 }),
    [token, from, today, reloadKey],
    onAuthExpired,
  );

  const recent = useMemo(() => {
    const map = new Map<string, { count: number; lastAt: string; avg: number }>();
    for (const item of list.data?.results ?? []) {
      const entry = map.get(item.supervisorId);
      if (entry) {
        entry.count += 1;
        entry.avg += item.durationSeconds;
        if (item.completedAt > entry.lastAt) entry.lastAt = item.completedAt;
      } else {
        map.set(item.supervisorId, {
          count: 1,
          avg: item.durationSeconds,
          lastAt: item.completedAt,
        });
      }
    }
    return map;
  }, [list.data]);

  const rows = supervisors.data?.supervisors ?? [];

  return (
    <>
      <TopBar
        title={ar.navSupervisors}
        subtitle={ar.supervisorsSubtitle}
        onOpenDrawer={onOpenDrawer}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={supervisors.reload}
            aria-label={ar.refresh}
            icon={<IconRefresh size={15} />}
          />
        }
      />
      <div className="page page-wide">
        {supervisors.error ? (
          <Notice tone="critical" role="alert">
            {supervisors.error}
          </Notice>
        ) : supervisors.loading && rows.length === 0 ? (
          <div className="tile-grid">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} height={112} radius={16} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<IconSupervisors size={26} />}
            title={ar.noSupervisorsTitle}
            text={ar.noSupervisorsText}
          />
        ) : (
          <ul className="tile-grid">
            {rows.map((row) => {
              const stats = recent.get(row.id);
              return (
                <li key={row.id} className="person-tile">
                  <span className="person-avatar" aria-hidden="true">
                    {row.name.trim().charAt(0)}
                  </span>
                  <span className="person-body">
                    <span className="person-name">{row.name}</span>
                    <span className="person-meta num">
                      {stats ? `${arDateTime(stats.lastAt)}` : '—'}
                    </span>
                  </span>
                  <span className="person-stats">
                    <span className="person-stat">
                      <span className="person-value num">{row.total}</span>
                      <span className="person-label">{ar.totalCompleted}</span>
                    </span>
                    {stats && (
                      <span className="person-stat">
                        <span className="person-value num">
                          {formatDuration(Math.round(stats.avg / stats.count))}
                        </span>
                        <span className="person-label">{ar.averageDuration}</span>
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------ Audit log */

const AUDIT_SCAN_LIMIT = 40;

/**
 * The audit log is assembled from data the API already exposes: the list is filtered to
 * edited reports, and each of those reports carries its own correction history. There is
 * no global audit endpoint, and inventing one would have meant changing the API.
 */
export function AuditScreen({
  token,
  onOpenDrawer,
  onAuthExpired,
  onOpenReport,
  reloadKey,
}: Omit<CommonProps, 'onEdit'>) {
  const today = riyadhBusinessDate();
  const from = useMemo(() => {
    const date = new Date(`${today}T12:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() - 89);
    return date.toISOString().slice(0, 10);
  }, [today]);

  const audit = useAsync(
    async () => {
      const list = await api.inspections(token, {
        from,
        to: today,
        edited: 'yes',
        limit: AUDIT_SCAN_LIMIT,
      });
      const reports = await Promise.all(
        list.results.map((item) =>
          api.report(token, item.id).then(
            (report) => ({ item, report }),
            () => null,
          ),
        ),
      );

      const events: (EditRecord & { roomNumber: string; inspectionId: string })[] = [];
      for (const entry of reports) {
        if (!entry) continue;
        for (const edit of entry.report.edits) {
          events.push({
            ...edit,
            roomNumber: entry.report.inspection.roomNumber,
            inspectionId: entry.report.inspection.id,
          });
        }
      }
      events.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
      return { events, scanned: list.results.length, total: list.total };
    },
    [token, from, today, reloadKey],
    onAuthExpired,
  );

  const events = audit.data?.events ?? [];

  return (
    <>
      <TopBar
        title={ar.navAudit}
        subtitle={ar.auditSubtitle}
        onOpenDrawer={onOpenDrawer}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={audit.reload}
            aria-label={ar.refresh}
            icon={<IconRefresh size={15} />}
          />
        }
      />
      <div className="page page-wide">
        {audit.error ? (
          <Notice tone="critical" role="alert">
            {audit.error}
          </Notice>
        ) : audit.loading && events.length === 0 ? (
          <div className="timeline-skeleton">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} height={92} radius={12} />
            ))}
          </div>
        ) : events.length === 0 ? (
          <EmptyState icon={<IconAudit size={26} />} title={ar.noAuditTitle} text={ar.noAuditText} />
        ) : (
          <>
            {(audit.data?.total ?? 0) > AUDIT_SCAN_LIMIT && (
              <p className="scope-note">{ar.auditScopeNote(AUDIT_SCAN_LIMIT)}</p>
            )}
            <div className="audit-list">
              <AuditTimeline edits={events} showRoom onOpen={onOpenReport} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

/* -------------------------------------------------------------- Settings */

/** One row: a label in a fixed lead column, its value immediately beside it. */
function SettingsRow({
  label,
  value,
  sub,
}: {
  label: string;
  value: ReactNode;
  sub?: string;
}) {
  return (
    <div className="settings-row">
      <span className="settings-label">{label}</span>
      <span className="settings-value">
        {value}
        {sub && <span className="settings-value-sub">{sub}</span>}
      </span>
    </div>
  );
}

function SettingsGroup({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="settings-group">
      <div className="settings-group-head">
        <h2 className="settings-group-title">{title}</h2>
        {description && <p className="settings-group-desc">{description}</p>}
      </div>
      <div className="settings-surface">{children}</div>
    </section>
  );
}

export function SettingsScreen({
  user,
  expiresAt,
  apiUrl,
  onOpenDrawer,
  onSignOut,
}: {
  user: { fullName: string; role: string };
  expiresAt: string | null;
  apiUrl: string;
  onOpenDrawer: () => void;
  onSignOut: () => void;
}) {
  const host = (() => {
    try {
      return new URL(apiUrl).host;
    } catch {
      return apiUrl;
    }
  })();

  return (
    <>
      <TopBar title={ar.navSettings} onOpenDrawer={onOpenDrawer} />
      <div className="page page-settings">
        <div className="settings-groups">
          <SettingsGroup title={ar.settingsAccount} description={ar.settingsIntro}>
            <div className="settings-identity">
              <span className="settings-avatar" aria-hidden="true">
                {user.fullName.trim().charAt(0)}
              </span>
              <span className="settings-identity-text">
                <span className="settings-identity-name">{user.fullName}</span>
                <span className="settings-identity-role">{ar.roleManagement}</span>
              </span>
            </div>
            <SettingsRow label={ar.settingsRole} value={ar.settingsRoleValue} />
            <SettingsRow
              label={ar.settingsSession}
              value={
                <>
                  <StatusIndicator tone="success">{ar.settingsSessionActive}</StatusIndicator>
                </>
              }
              sub={expiresAt ? ar.settingsSessionUntil(arDateTime(expiresAt)) : undefined}
            />
          </SettingsGroup>

          <SettingsGroup title={ar.settingsDisplay} description={ar.settingsDisplayDesc}>
            <SettingsRow label={ar.settingsInterface} value={ar.settingsInterfaceValue} />
            <SettingsRow label={ar.settingsNumerals} value={ar.settingsNumeralsValue} />
          </SettingsGroup>

          <SettingsGroup title={ar.settingsRegion} description={ar.settingsRegionDesc}>
            <SettingsRow label={ar.settingsTimezone} value={ar.settingsTimezoneValue} />
            <SettingsRow label={ar.settingsBusinessDay} value={ar.settingsBusinessDayValue} />
          </SettingsGroup>

          <SettingsGroup title={ar.settingsApplication}>
            <SettingsRow
              label={ar.settingsApi}
              value={<StatusIndicator tone="success">{ar.settingsApiConnected}</StatusIndicator>}
              sub={host}
            />
            <SettingsRow label={ar.settingsEnvironment} value={ar.settingsEnvironmentValue} />
          </SettingsGroup>

          <SettingsGroup title={ar.settingsSessionGroup}>
            <div className="settings-row settings-row-action">
              <span className="settings-label">
                <span className="settings-action-title">{ar.settingsSignOutTitle}</span>
                <span className="settings-action-desc">{ar.settingsSignOutNote}</span>
              </span>
              <Button variant="outline" onClick={onSignOut} icon={<IconLogout size={15} />}>
                {ar.signOut}
              </Button>
            </div>
          </SettingsGroup>
        </div>
      </div>
    </>
  );
}
